import { useMutation } from "@apollo/client/react";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { GetRecordComposerDefinitionDetailQuery } from "~/common/gqls";
import type { MutationRefetchQueries } from "~/common/types";
import {
  UpsertRelationComposerDefinitionDocument,
  type UpsertRelationComposerDefinitionMutation,
  type UpsertRelationComposerDefinitionMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { PropertySchemaSetFormData } from "~/features/dataImportProjects/types";
import { PropertySchemasMapper } from "~/features/dataImportProjects/utils/propertySchemasMapper";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { useDefinitionJsonManager } from "./useDefinitionJsonManager";

export function useRelationComposerDefinitionForm(onSubmit: () => void, refetchQueriesOnEdit: MutationRefetchQueries) {
  const { t } = useTranslation();
  const [processing, setProcessing] = React.useState(false);
  const [upsertRelationComposerDef] = useMutation<
    UpsertRelationComposerDefinitionMutation,
    UpsertRelationComposerDefinitionMutationVariables
  >(UpsertRelationComposerDefinitionDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });
  const { tenantId } = useTenant();
  const [name, setName] = React.useState("");

  const jsonManager = useDefinitionJsonManager({ serverDefinition: undefined });
  const { initFromServer, workingJson } = jsonManager;

  const resetByRelationComposerDefinition = useCallback(
    (data: GetRecordComposerDefinitionDetailQuery) => {
      const composerDef = data?.recordComposerDefinitions?.[0];
      if (!composerDef) {
        return;
      }

      initFromServer(composerDef.definition as Record<string, unknown>);
      setName(composerDef.name);
    },
    [initFromServer],
  );

  const upsertRelationComposerDefinition = useCallback(
    async (
      importProjectId: string,
      evidenceSchemaSet: PropertySchemaSetFormData | undefined,
      evidenceSetIds: string[],
      composerDefId?: string,
      propertySchemaSetId?: string,
    ) => {
      if (!tenantId) {
        toast.error(translateError(t, new Error("Tenant is not resolved")));
        return false;
      }

      const input: UpsertRelationComposerDefinitionMutationVariables["input"] = {
        tenantId,
        id: composerDefId,
        importProjectId,
        name,
        definition: workingJson,
        evidenceSetIds,
        propertySchemaSet: {
          id: propertySchemaSetId,
          schemas:
            evidenceSchemaSet?.propertySchemaAssociations.map((psa) =>
              PropertySchemasMapper.propertySchemaAssociation.copyWithoutId(psa),
            ) ?? [],
        },
      };

      setProcessing(true);
      try {
        const res = await upsertRelationComposerDef({ variables: { input } });
        if (res.data?.cnutUpsertRelationComposerDefinition.__typename === "ValidationError") {
          const e = res.data.cnutUpsertRelationComposerDefinition;
          toast.error(`${e.code}: ${e.message}`);
          return false;
        }
        toast.success(t("feature.common.createdSuccessfully", { model: "relationComposer" }));
        onSubmit();
        return true;
      } finally {
        setProcessing(false);
      }
    },
    [t, setProcessing, upsertRelationComposerDef, onSubmit, tenantId, name, workingJson],
  );

  return {
    name,
    setName,
    resetByRelationComposerDefinition,
    upsertRelationComposerDefinition,
    processing,
    jsonManager,
  };
}
